import {ChangeDetectionStrategy, Component, OnDestroy, OnInit} from '@angular/core';
import {ObjectStore, RequestError, Store} from '@softline/core';
import {BehaviorSubject, of, Subscription} from 'rxjs';
import {
  BackNavigable,
  BackNavigationService, CommandStore,
  showRequestErrors,
  Scan,
  ScannerStore, SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_SCANNER, SOFTLINE_FEATURE_TITLE, TitleStore
} from '@softline/application';
import {Router} from '@angular/router';
import {SOFTLINE_FEATURE_PRODUCT_SCAN} from '../../product-scan.shared';
import {ProductStore} from '../../store/product.store';
import {skip} from 'rxjs/operators';
import {MessageBarStore, SOFTLINE_FEATURE_MESSAGE_BAR} from '@softline/ui-core';
import {ItemNotFoundError, ValidateScanError} from '../../services/product-resource.service';
import {CommonModule} from '@angular/common';

import { InputComponent } from '@softline/ui-core';
import { LoadingSpinnerComponent } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
    selector: 'lib-find-product',
    imports: [CommonModule,
    InputComponent,
    LoadingSpinnerComponent,
    TranslatePipe],
    templateUrl: './find-product.component.html',
    styleUrls: ['./find-product.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FindProductComponent implements OnInit, OnDestroy, BackNavigable {

  private subscription?: Subscription;
  readonly title$ = of('#PRODUCT_SCAN.TITLE');
  readonly canNavigateBack$ = of(true);

  readonly loading$ = this.store.observe(SOFTLINE_FEATURE_PRODUCT_SCAN, ProductStore.getters.data);
  readonly manualInput$ = new BehaviorSubject<string | null>(null);

  constructor(private store: Store,
              protected router: Router,
              private backNavigationService: BackNavigationService) { }

  ngOnInit(): void {
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '#PRODUCT_SCAN.TITLE');
    this.backNavigationService.set(this);

    this.subscription = this.store.observe(SOFTLINE_FEATURE_SCANNER, ScannerStore.getters.latest).pipe(skip(1))
      .subscribe(async o => {
        try {
          await this.scanAndNavigate(o);
        } catch (e: any) {
          if (e instanceof RequestError) {
            await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.error, '#PRODUCT_SCAN.MESSAGES.ERROR.LOAD');
          } else {
            showRequestErrors(this.store, e);
          }
        }
      });

    this.store.commit(SOFTLINE_FEATURE_PRODUCT_SCAN, ObjectStore.mutations.unset);
  }

  ngOnDestroy() {
    if (this.subscription && !this.subscription.closed)
      this.subscription.unsubscribe();
    this.subscription = undefined;

    this.backNavigationService.set(undefined);
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
  }

  async startScan(): Promise<void> {
    try {
      await this.store.dispatch(SOFTLINE_FEATURE_SCANNER, ScannerStore.actions.scan, {labelType: ['ean8', 'qrcode', 'ean13', 'itf', 'ean128']});
    } catch (e: any) {
      if (e instanceof RequestError) {
        await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.error, {
          title: '#MESSAGES.ERROR.TITLE',
          message: '#MESSAGES.ERROR.SCAN_ERROR'
        });
      } else {
        showRequestErrors(this.store, e);
      }
    }
  }

  // tslint:disable-next-line:variable-name
  async findProduct(number: string | number): Promise<void> {
    try {
      const item = await this.store.dispatch(SOFTLINE_FEATURE_PRODUCT_SCAN, ProductStore.actions.loadNumber, {number});

      if (!item)
        return;

      await this.router.navigate(['artikelinfo/item']);
    } catch (e: any) {
      if (e instanceof ItemNotFoundError)
        await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.error, '#PRODUCT_SCAN.MESSAGES.ERROR.LOAD');
      else if (e instanceof ValidateScanError)
        await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.error, '#PRODUCT_SCAN.MESSAGES.ERROR.SCAN');
      else
        showRequestErrors(this.store, e);
    }
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/']);
  }

  private async scanAndNavigate(scan?: Scan): Promise<void> {
    if (!scan)
      return;

    try {
      const scanResult = await this.store.dispatch(SOFTLINE_FEATURE_PRODUCT_SCAN, ProductStore.actions.loadScan, { scan });

      if (!scanResult)
        return;

      await this.router.navigate(['artikelinfo/item']);
    } catch (e: any) {
      if (e instanceof ItemNotFoundError)
        await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.error, '#PRODUCT_SCAN.MESSAGES.ERROR.LOAD');
      else if (e instanceof ValidateScanError)
        await this.store.dispatch(SOFTLINE_FEATURE_MESSAGE_BAR, MessageBarStore.actions.error, '#PRODUCT_SCAN.MESSAGES.ERROR.SCAN');
      else
        showRequestErrors(this.store, e);
    }
  }
}
